import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import ChunkErrorBoundary from "./admin/ChunkErrorBoundary";
import CircularProgress from "@mui/material/CircularProgress";
import Login from "./screens/Login";
import EmpDocumentDashboard from "./screens/EmpDocumentDashboard";
import ViewDocument from "./screens/DocumentView";
import PostLogin from "./screens/PostLogin";
import EmployeeSurvey from "./screens/EmployeeSurvey";
import EmployeeSurveyResult from "./screens/EmployeeSurveyResult";
import EmployeeSurveyResultBasic from "./screens/EmployeeSurveyResultBasic";
import EmployeeSurveyResultAnnual from "./screens/EmployeeSurveyResultAnnual";
import EmployeeTraining from "./screens/EmployeeTraining";
import CleaningLog from "./screens/CleaningLog";
import EmployeeApp from "./employee/EmployeeApp";
import { getCurrentUser, isMaintenanceOnly, isStoreDevice } from "./admin/useCurrentUser";

const AdminArea = lazy(() => import("./admin/AdminRoutes"));
const PublicFormPage = lazy(() => import("./admin/screens/forms/PublicFormPage"));
const CounterArea = lazy(() => import("./admin/screens/workorders/CounterArea"));
const MaintenanceStandalone = lazy(() => import("./admin/screens/maintenance/MaintenanceStandalone"));
const VendorPortal = lazy(() => import("./admin/screens/maintenance/vendor/VendorPortal"));

/**
 * The Work Orders counter tablet (store device, user_type 10) gets its own
 * /work-orders area without the admin shell. Everyone else reaches the same
 * URLs through the shell (AdminRoutes). Sign-in ends with a full page load,
 * so this is read fresh for whoever is signed in.
 */
const signedInAsStoreDevice = () => isStoreDevice(getCurrentUser());

/**
 * A maintenance-only account (role 11) gets its own /maintenance area and
 * nothing else: every other address sends it there, so the admin shell, the
 * legacy sidebar and ProtectedRoute never render for it.
 */
const signedInAsMaintenanceOnly = () => isMaintenanceOnly(getCurrentUser());

/** Redirect that keeps the query string (e.g. /employee/log?id=42). */
const RedirectWithSearch = ({ to }: { to: string }) => (
  <Navigate to={`${to}${window.location.search}`} replace />
);

/** Minimal loader while the shell chunk (now serving /dashboard) resolves. */
const ShellFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
    <CircularProgress size={28} />
  </div>
);

/** /employee/edit/:id -> /employees/edit/:id, keeping ?ref= etc. */
const RedirectEditEmployee = () => {
  const parts = window.location.pathname.split("/");
  const id = parts[parts.length - 1];
  return <Navigate to={`/employees/edit/${id}${window.location.search}`} replace />;
};

/** Old /admin bookmarks: strip the prefix so /admin/org/store -> /org/store. */
const StripAdminPrefix = () => {
  const stripped = window.location.pathname.replace(/^\/admin/, "");
  const target = stripped && stripped !== "/" ? stripped : "/dashboard";
  return <Navigate to={`${target}${window.location.search}`} replace />;
};

const App = () => {
  return (
    <div className="App">
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/verifyemail" element={<PostLogin />} />
          {signedInAsStoreDevice() && (
            <Route
              path="/work-orders/*"
              element={
                <ChunkErrorBoundary>
                  <Suspense fallback={<ShellFallback />}>
                    <CounterArea />
                  </Suspense>
                </ChunkErrorBoundary>
              }
            />
          )}
          {signedInAsMaintenanceOnly() && (
            <Route
              path="/maintenance/*"
              element={
                <ChunkErrorBoundary>
                  <Suspense fallback={<ShellFallback />}>
                    <MaintenanceStandalone />
                  </Suspense>
                </ChunkErrorBoundary>
              }
            />
          )}
          {/* The accountless vendor portal: the secret is in the URL fragment, and
              the page never uses the internal login or its interceptors. */}
          <Route
            path="/vendor-access"
            element={
              <ChunkErrorBoundary>
                <Suspense fallback={<ShellFallback />}>
                  <VendorPortal />
                </Suspense>
              </ChunkErrorBoundary>
            }
          />
          <Route path="/forms/cleaning-log" element={<CleaningLog />} />
          <Route
            path="/f/:token"
            element={
              <ChunkErrorBoundary>
                <Suspense fallback={<ShellFallback />}>
                  <PublicFormPage />
                </Suspense>
              </ChunkErrorBoundary>
            }
          />

          <Route path="/employee" element={<EmployeeApp />}>
            <Route path="document-dashboard/:id" element={<EmpDocumentDashboard />} />
            <Route path="view-document/:token" element={<ViewDocument />} />
            <Route path="survey/:employeeId/:surveyId" element={<EmployeeSurvey />} />
            <Route path="training" element={<EmployeeTraining />} />
            <Route path="surveyResults/:employeeId/:surveyId" element={<EmployeeSurveyResult />} />
            <Route path="surveyResultsBasic/:employeeId/:surveyId" element={<EmployeeSurveyResultBasic />} />
            <Route path="surveyResultsAnnual/:employeeId/:surveyId" element={<EmployeeSurveyResultAnnual />} />
          </Route>

          {/* ------ protected routes begin ----------- */}

          {/* /dashboard and /directory are served by the shell (AdminRoutes). */}
          <Route
            path="/employeedirectory"
            element={<Navigate to="/directory" replace />}
          />
          <Route
            path="/server-productivity"
            element={<Navigate to="/bi" replace />}
          />
          {/* /employee/documents/:id and the kept orphans are served by the shell. */}
          <Route
            path="/employee/add"
            element={<Navigate to="/employees/add" replace />}
          />
          <Route
            path="/edit"
            element={<Navigate to="/employees/edit" replace />}
          />

          <Route
            path="/employee/remove"
            element={<Navigate to="/employees/remove" replace />}
          />
          <Route
            path="/employee/promote"
            element={<Navigate to="/employees/promote" replace />}
          />
          {/* Unified app shell (lazy). Mounted on the root splat: explicit
              sibling routes above/below outrank it in RR6, so only unmatched
              paths fall through to the shell. Old /admin bookmarks are handled
              by the StripAdminPrefix redirect below. */}
          <Route
            path="/*"
            element={
              signedInAsMaintenanceOnly() ? (
                <Navigate to="/maintenance" replace />
              ) : (
                <ChunkErrorBoundary>
                  <Suspense fallback={<ShellFallback />}>
                    <AdminArea />
                  </Suspense>
                </ChunkErrorBoundary>
              )
            }
          />
          <Route path="/admin/*" element={<StripAdminPrefix />} />
          <Route
            path="/organization/master"
            element={<Navigate to="/org/organization" replace />}
          />
          <Route
            path="/group/master"
            element={<Navigate to="/org/group" replace />}
          />
          <Route
            path="/store/master"
            element={<Navigate to="/org/store" replace />}
          />
          <Route
            path="/invite/master"
            element={<Navigate to="/org/invite" replace />}
          />
          <Route
            path="/employee/transfer"
            element={<Navigate to="/employees/transfer" replace />}
          />
          <Route
            path="/employee/borrow"
            element={<Navigate to="/employees/borrow" replace />}
          />
          <Route
            path="/employee/log"
            element={<RedirectWithSearch to="/employees/log" />}
          />
          <Route
            path="/employee/ind-log"
            element={<RedirectWithSearch to="/employees/ind-log" />}
          />
          <Route
            path="/employee/activate"
            element={<Navigate to="/employees/activate" replace />}
          />
          <Route path="/employee/edit/:id" element={<RedirectEditEmployee />} />
          <Route
            path="/adddocument"
            element={<Navigate to="/documents/add-bulk" replace />}
          />
          <Route
            path="/adddocumentnew"
            element={<Navigate to="/documents/add" replace />}
          />
          <Route
            path="/position/master"
            element={<Navigate to="/org/position" replace />}
          />
          <Route
            path="/assigndocument"
            element={<Navigate to="/documents/assign" replace />}
          />
          <Route
            path="/outstandingdocs"
            element={<Navigate to="/reports/outstanding-docs" replace />}
          />
          <Route
            path="/employeestatus"
            element={<Navigate to="/reports/employee-status" replace />}
          />
          <Route
            path="/showdocument"
            element={<Navigate to="/documents/show" replace />}
          />
          <Route
            path="/specificdoc"
            element={<Navigate to="/reports/specific-docs" replace />}
          />
          <Route
            path="/csvreport"
            element={<Navigate to="/reports/csv" replace />}
          />
          <Route
            path="/experienceletter"
            element={<Navigate to="/reports/experience-letter" replace />}
          />
          <Route
            path="/verifydocument"
            element={<Navigate to="/documents/verify" replace />}
          />
          <Route
            path="/sendResendDocuments"
            element={<Navigate to="/documents/send" replace />}
          />
        </Routes>
      </Router>
    </div>
  );
};

export default App;
